import React, { useState, useEffect, useMemo, useCallback, useRef } from 'react';
import { BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, Legend, ResponsiveContainer, Line } from 'recharts';
import { AlertTriangle, Loader, Eye, RefreshCw } from 'lucide-react';
import L from 'leaflet';
import { signOut } from 'firebase/auth';
import { doc, updateDoc, writeBatch, addDoc, collection, serverTimestamp, deleteDoc, arrayUnion, arrayRemove } from 'firebase/firestore';
import { ref, uploadBytes, getDownloadURL, deleteObject } from "firebase/storage";
import { v4 as uuidv4 } from 'uuid';

// --- IMPORTAÇÃO DE HOOKS, COMPONENTES, CONSTANTES E UTILITÁRIOS ---
import { useAuth } from './hooks/useAuth';
import { useAppData, useAppCalculations } from './hooks/useAppData';
import { useAppHandlers } from './hooks/useAppHandlers';
import { useTrades } from './hooks/useTrades';
import GlobalStyles from './components/lib/GlobalStyles';
import Header from './components/layout/Header';
import MainTabs, { getFirstAvailableMainTab, getVisibleMainTabs } from './components/layout/MainTabs';
import DashboardSubTabs from './components/layout/DashboardSubTabs';
import { BrowserRouter as Router, Routes, Route, Navigate } from 'react-router-dom';
import LoginPage from './components/pages/LoginPage';
import SignUpPage from './components/pages/SignUpPage';
import PendingApprovalPage from './components/pages/PendingApprovalPage';
import TradesPage from './components/pages/TradesPage';
import ProjectsPage from './components/pages/ProjectsPage';
import PendenciesPage from './components/pages/PendenciesPage';
import NotificationsPage from './components/pages/NotificationsPage';
import NetworkMonitoringPage from './components/pages/NetworkMonitoringPage';
import CoveragePage from './components/pages/CoveragePage';
import WhatsNewModal from './components/modals/WhatsNewModal';
import ProjectModal from './components/modals/ProjectModal';
import PendencyModal from './components/modals/PendencyModal';
import NewPendencyDialog from './components/modals/NewPendencyDialog';
import TradeModal from './components/modals/TradeModal';
import TradeShareModal from './components/trades/TradeShareModal';
import NewNotificationModal from './components/modals/NewNotificationModal';
import AdminPanelModal from './components/modals/AdminPanelModal';
import AboutModal from './components/modals/AboutModal';
import UserProfileModal from './components/modals/UserProfileModal';
import EffortCalculatorModal from './components/modals/EffortCalculatorModal';
import DuplicatePolesModal from './components/modals/DuplicatePolesModal';
import CustomTooltip from './utils/CustomTooltip';
import NotificationModal from './components/modals/NotificationModal';
import NotificationsModal from './components/modals/NotificationsModal';
import BulkNotificationModal from './components/modals/BulkNotificationModal';
import StatsDisplay from './components/dashboard/StatsDisplay';
import CommercialDashboard from './components/dashboard/CommercialDashboard';
import ChurnDashboard from './components/dashboard/ChurnDashboard';
import QualityDashboard from './components/dashboard/QualityDashboard';
import CelebrationAnimation from './components/common/CelebrationAnimation';
import StatusBar from './components/layout/StatusBar';
import { APP_VERSION, WHATS_NEW_CONTENT, appId } from './constants/appConstants.js';
import { formatCompactCurrency } from './utils/formatters.js';
import { generatePendencyId } from './utils/pendencyUtils.js';
import { logActivity } from './services/activityLogService.js';
import { refreshDashboardStats, triggerBiCalculation } from './services/apiService';

// --- CONFIG LEAFLET ---
import iconRetinaUrl from 'leaflet/dist/images/marker-icon-2x.png';
import iconUrl from 'leaflet/dist/images/marker-icon.png';
import shadowUrl from 'leaflet/dist/images/marker-shadow.png';
delete L.Icon.Default.prototype._getIconUrl;
L.Icon.Default.mergeOptions({ iconRetinaUrl, iconUrl, shadowUrl });

const DASHBOARD_SUB_TABS = [
    { id: 'infra', label: 'Infraestrutura (Postes)', allowedRoles: ['administrador'] },
    { id: 'comercial', label: 'Comercial & Vendas', allowedRoles: ['administrador'] },
    { id: 'churn', label: 'Retenção & Churn', allowedRoles: ['administrador'] },
    { id: 'qualidade', label: 'Qualidade', allowedRoles: ['administrador', 'comercial'] },
];

/**
 * Componente Principal da Aplicação (Autenticado).
 */
export default function MainApp() {
    const [theme, setTheme] = useState('light');
    // Inicializar como null evita flash de páginas inacessíveis.
    // A aba inicial correta é definida no useEffect abaixo, após userData carregar.
    const [activeTab, setActiveTab] = useState(null);
    const [dashboardSubTab, setDashboardSubTab] = useState('infra');
    const [modalMessageState, setModalMessageState] = useState(null);
    const [operationSuccess, setOperationSuccess] = useState(false);

    // Função para definir a mensagem do modal, com detecção automática de erros/sucessos
    const setModalMessage = useCallback((msg) => {
        if (!msg) {
            setModalMessageState(null);
            return;
        }
        if (typeof msg === 'string') {
            const lower = msg.toLowerCase();
            const isError = lower.includes('erro') || 
                            lower.includes('falha') || 
                            lower.includes('inválid') || 
                            lower.includes('já existe') || 
                            lower.includes('não foi possível') || 
                            lower.includes('não pode') || 
                            lower.includes('prosseguir') ||
                            lower.includes('obrigatório') ||
                            lower.includes('cancelad') ||
                            lower.includes('negada');
            setModalMessageState({
                text: msg,
                type: isError ? 'error' : 'success'
            });
        } else if (typeof msg === 'object') {
            setModalMessageState({
                text: msg.text || '',
                type: msg.type || 'success'
            });
        }
    }, []);
    const [isProcessing, setIsProcessing] = useState(false);
    const [isProjectModalOpen, setIsProjectModalOpen] = useState(false);
    const [isPendencyModalOpen, setIsPendencyModalOpen] = useState(false);
    const [isTradeModalOpen, setIsTradeModalOpen] = useState(false);
    const [isTradeShareModalOpen, setIsTradeShareModalOpen] = useState(false);
    const [currentTrade, setCurrentTrade] = useState(null);
    const [tradeSearchTerm, setTradeSearchTerm] = useState('');
    const [tradeToShare, setTradeToShare] = useState(null);
    const [isNotificationModalOpen, setIsNotificationModalOpen] = useState(false);
    const [isBulkNotificationModalOpen, setIsBulkNotificationModalOpen] = useState(false);
    const [currentNotification, setCurrentNotification] = useState(null);
    const [isAdminPanelOpen, setIsAdminPanelOpen] = useState(false);
    const [isProfileModalOpen, setIsProfileModalOpen] = useState(false);
    const [isEffortCalculatorOpen, setIsEffortCalculatorOpen] = useState(false);
    const [isNotificationsModalOpen, setIsNotificationsModalOpen] = useState(false);
    const [isAboutModalOpen, setIsAboutModalOpen] = useState(false);
    const [isDuplicatePolesModalOpen, setIsDuplicatePolesModalOpen] = useState(false);
    const [isWhatsNewModalOpen, setIsWhatsNewModalOpen] = useState(false);
    const [currentProject, setCurrentProject] = useState(null);
    const [currentPendency, setCurrentPendency] = useState(null);
    const [newPendencyInfo, setNewPendencyInfo] = useState(null);
    const [showOnlyPending, setShowOnlyPending] = useState(true);
    const [showDashboardData, setShowDashboardData] = useState(false);
    const [, setEnableDashboardTransition] = useState(false);
    const [showCelebration, setShowCelebration] = useState(false);
    const [considerNonRejected, setConsiderNonRejected] = useState(() => {
        return localStorage.getItem('dashboard_considerNonRejected') === 'true';
    });

    const handleToggleConsiderNonRejected = (e) => {
        const newValue = e.target.checked;
        setConsiderNonRejected(newValue);
        localStorage.setItem('dashboard_considerNonRejected', newValue);
    };

    const { auth, user, userData, authLoading, db, storage } = useAuth();
    const { loading: dataLoading, projects, pendencies, usersList, userNotifications, irregularityNotifications, citiesData, setCitiesData, settings, connectionError, biStats } = useAppData(db, user, userData);
    const { trades: allTrades, loading: tradesLoading } = useTrades(db, user);

    const triggerCelebration = () => setShowCelebration(true);

    const handlePendencyCreated = (pendencyId) => {
        closePendencyModal();
        setNewPendencyInfo(pendencyId);
    };

    const handleDuplicateClick = useCallback(() => setIsDuplicatePolesModalOpen(true), []);

    const handlers = useAppHandlers({
        db,
        storage,
        userData,
        setIsProcessing,
        setModalMessage,
        setOperationSuccess,
        triggerCelebration,
        onPendencyCreated: handlePendencyCreated,
    });

    const { appStats, chartData, projectStatusChartData, usersMap, enrichedPendencies, pendencyStatusChartData, enrichedProjects, calculatedCitiesData } = useAppCalculations({
        projects,
        pendencies, usersList, citiesData, settings,
        showOnlyPending,
        includeNonRejected: considerNonRejected
    });

    const visibleDashboardSubTabs = useMemo(() => {
        return DASHBOARD_SUB_TABS.filter(tab => tab.allowedRoles.includes(userData?.role));
    }, [userData?.role]);

    const shouldMaskDashboardData = userData?.role === 'administrador' && !showDashboardData;

    const hasUrgentNotifications = useMemo(() => {
        if (!irregularityNotifications || irregularityNotifications.length === 0) return false;

        const today = new Date();
        today.setHours(0, 0, 0, 0);

        return irregularityNotifications.some(notification => {
            if (!notification) return false;

            const { status = 'pendente', dueDate } = notification;
            if (!dueDate) return false;

            const isFinalized = status?.toLowerCase() === 'finalizada';
            if (isFinalized) return false;

            const dueDateObj = new Date(dueDate + 'T00:00:00');
            const timeDiff = dueDateObj.getTime() - today.getTime();
            const daysRemaining = Math.ceil(timeDiff / (1000 * 3600 * 24));

            return daysRemaining <= 3;
        });
    }, [irregularityNotifications]);

    useEffect(() => {
        document.documentElement.classList.toggle('dark', theme === 'dark');

        // Expõe a função global para o Android poder consultar o tema atual
        window.getTheme = function() {
            return theme;
        };

        // Cleanup
        return () => {
            delete window.getTheme;
        };
    }, [theme]);

    useEffect(() => {
        if (userData) {
            const userTheme = userData.theme || 'light';
            setTheme(userTheme);
            if (window.Android && window.Android.updateTheme) {
                console.log(`[Android Bridge] Calling updateTheme(${userTheme === 'dark'}) from useEffect`);
                window.Android.updateTheme(userTheme === 'dark');
            } else {
                console.log("[Android Bridge] window.Android.updateTheme not available on initialization.");
            }
            // Se activeTab for null (carregamento inicial) ou inacessível, define a primeira aba disponível
            const canAccessActiveTab = activeTab && getVisibleMainTabs(userData.role).some(tab => tab.id === activeTab);
            if (!canAccessActiveTab) {
                const firstAvailableTab = getFirstAvailableMainTab(userData.role);
                if (firstAvailableTab) {
                    setActiveTab(firstAvailableTab);
                }
            }
        }
    }, [userData, activeTab]);

    useEffect(() => {
        if (user && userData?.role !== 'pending' && localStorage.getItem('whatsNewLastSeenVersion') !== APP_VERSION) {
            setIsWhatsNewModalOpen(true);
        }
    }, [user, userData]);



    // Track displayed notifications to only notify Android of new ones
    const previousNotificationIds = useRef(null);

    useEffect(() => {
        if (!userNotifications) return;

        if (previousNotificationIds.current === null) {
            // First load: just initialize the set to avoid sending all existing unread notifications as "new"
            previousNotificationIds.current = new Set(userNotifications.map(n => n.id));
            return;
        }

        const getTitleForType = (type) => {
            switch (type) {
                case 'newUser': return 'Novo Usuário';
                case 'pendencyAssigned': return 'Pendência Atribuída';
                case 'projectUpdate': return 'Atualização de Projeto';
                case 'projectMention': return 'Menção em Projeto';
                case 'pendencyUpdate': return 'Atualização de Pendência';
                case 'notificationClosed': return 'Notificação Finalizada';
                case 'notificationNew': return 'Nova Notificação';
                default: return 'Nova Notificação';
            }
        };

        userNotifications.forEach(notif => {
            if (!previousNotificationIds.current.has(notif.id)) {
                previousNotificationIds.current.add(notif.id);
                if (window.Android && window.Android.onNewNotification) {
                    const title = getTitleForType(notif.type);
                    const url = `${window.location.origin}/?openNotification=${notif.id}`;

                    const androidNotification = {
                        id: notif.id,
                        title: title,
                        description: notif.message,
                        url: url
                    };

                    try {
                        window.Android.onNewNotification(JSON.stringify(androidNotification));
                    } catch (e) {
                        console.error("[Android Bridge] Error sending notification:", e);
                    }
                }
            }
        });
    }, [userNotifications]);

    useEffect(() => {
        if (activeTab === 'grafico') {
            const canAccessDashboardSubTab = visibleDashboardSubTabs.some(tab => tab.id === dashboardSubTab);
            if (!canAccessDashboardSubTab && visibleDashboardSubTabs[0]) {
                setDashboardSubTab(visibleDashboardSubTabs[0].id);
            }
            const timer = setTimeout(() => setEnableDashboardTransition(true), 50);
            return () => clearTimeout(timer);
        } else {
            setEnableDashboardTransition(false);
        }
    }, [activeTab, dashboardSubTab, visibleDashboardSubTabs]);

    useEffect(() => {
        if (!isProjectModalOpen) return;
        if (currentProject?.id) {
            const atualizado = projects.find(p => p.id === currentProject.id);
            if (atualizado) setCurrentProject(atualizado);
        }
    }, [projects, currentProject?.id, isProjectModalOpen]);

    useEffect(() => {
        if (!isPendencyModalOpen) return;
        if (currentPendency?.id) {
            const atualizado = pendencies.find(p => p.id === currentPendency.id);
            if (atualizado) setCurrentPendency(atualizado);
        }
    }, [pendencies, currentPendency?.id, isPendencyModalOpen]);

    const handleLogout = async () => auth && await signOut(auth);

    const handleThemeChange = async (newTheme) => {
        if (!db || !user) return;
        const oldTheme = theme;
        setTheme(newTheme);
        if (window.Android && window.Android.updateTheme) {
            console.log(`[Android Bridge] Calling updateTheme(${newTheme === 'dark'}) from handleThemeChange`);
            window.Android.updateTheme(newTheme === 'dark');
        } else {
            console.log("[Android Bridge] window.Android.updateTheme not available on change.");
        }
        try {
            await updateDoc(doc(db, 'users', user.uid), { theme: newTheme });
        } catch (error) {
            setTheme(oldTheme);
            if (window.Android && window.Android.updateTheme) {
                console.log(`[Android Bridge] Calling updateTheme(${oldTheme === 'dark'}) to revert`);
                window.Android.updateTheme(oldTheme === 'dark');
            }
            setModalMessage("Não foi possível salvar sua preferência de tema.");
        }
    };

    const handleRefreshOzmap = async () => {
        setIsProcessing(true);
        try {
            await refreshDashboardStats();
            setModalMessage("Atualização solicitada! Os dados serão atualizados em breve.");
        } catch (error) {
            setModalMessage("Erro ao solicitar atualização: " + error.message);
        } finally {
            setIsProcessing(false);
        }
    };

    const handleRefreshBi = async () => {
        setIsProcessing(true);
        try {
            await triggerBiCalculation();
            setModalMessage("Atualização de BI iniciada! Os dados comerciais estarão disponíveis em breve.");
        } catch (error) {
            setModalMessage("Erro ao iniciar atualização comercial: " + error.message);
        } finally {
            setIsProcessing(false);
        }
    };

    const handleSaveChanges = async () => {
        if (!db) return;
        setIsProcessing(true);
        try {
            const batch = writeBatch(db);
            citiesData.forEach(city => {
                const docRef = doc(db, `artifacts/${appId}/public/data/cities`, city.id);
                // Only saving settings now, since city stats are automated
                batch.set(docRef, { nome: city.nome }, { merge: true });
            });
            await batch.commit();
            setModalMessage("Configurações salvas com sucesso!");
        } catch (err) {
            setModalMessage("Ocorreu um erro ao salvar.");
        } finally {
            setIsProcessing(false);
        }
    };

    const openProjectModal = useCallback((project = null) => {
        if (project?.id) {
            logActivity('view', 'project', project.id, null, project.protocolo);
            // Replaces the onProjectView Firestore trigger
            if (user?.uid) {
                const projectRef = doc(db, `artifacts/${appId}/public/data/projects/${project.id}`);
                updateDoc(projectRef, {
                    viewers: arrayUnion(user.uid)
                }).catch(err => console.error("Falha ao atualizar visualizadores do projeto:", err));
            }
        }
        setCurrentProject(project);
        setIsProjectModalOpen(true);
    }, [db, user?.uid]);
    const closeProjectModal = useCallback(() => { setIsProjectModalOpen(false); setCurrentProject(null); setIsProcessing(false); }, []);
    // Optimized: Wrapped in useCallback to ensure a stable reference, preventing unnecessary re-renders in child components (e.g., PendenciesPage).
    const openPendencyModal = useCallback((pendency = null) => {
        if (pendency?.id) {
            const pendencyId = generatePendencyId(pendency.id);
            logActivity('view', 'pendency', pendencyId, null, pendencyId);
        }
        setCurrentPendency(pendency);
        setIsPendencyModalOpen(true);
    }, []);
    const closePendencyModal = useCallback(() => { setIsPendencyModalOpen(false); setCurrentPendency(null); setIsProcessing(false); }, []);

    const openTradeModal = useCallback((trade = null) => {
        if (trade?.id) logActivity('view', 'trade', trade.id, null, trade.serviceNote);
        setCurrentTrade(trade);
        setIsTradeModalOpen(true);
    }, []);
    const closeTradeModal = useCallback((searchNum = '') => {
        setIsTradeModalOpen(false);
        setCurrentTrade(null);
        setIsProcessing(false);
        if (searchNum && typeof searchNum === 'string') {
            setTradeSearchTerm(searchNum);
        }
    }, []);

    const openTradeShareModal = useCallback((trade) => {
        setTradeToShare(trade);
        setIsTradeShareModalOpen(true);
    }, []);
    const closeTradeShareModal = useCallback(() => { setIsTradeShareModalOpen(false); setTradeToShare(null); }, []);

    const openNotificationModal = useCallback((notification = null) => {
        if (notification?.id) logActivity('view', 'notification', notification.id, null, notification.notificationNumber);
        setCurrentNotification(notification);
        setIsNotificationModalOpen(true);
    }, []);
    const closeNotificationModal = useCallback(() => { setIsNotificationModalOpen(false); setCurrentNotification(null); setIsProcessing(false); }, []);

    const handleSaveTrade = async (formData, filesToUpload, tradeId, isPartialUpdate = false) => {
        setIsProcessing(true);
        try {
            const activityType = tradeId ? 'update' : 'create';

            if (isPartialUpdate && tradeId) {
                const tradeRef = doc(db, `artifacts/${appId}/public/data/trades`, tradeId);
                await updateDoc(tradeRef, {
                    technicianNotes: formData.technicianNotes,
                    updatedAt: serverTimestamp(),
                    updatedBy: user.uid,
                });
                await logActivity('update', 'trade', tradeId, { partial: true });
                setModalMessage('Observações salvas com sucesso!');
                closeTradeModal();
            } else {
                const fromInputDate = (dateString) => {
                    if (!dateString || typeof dateString !== 'string') return '';
                    const parts = dateString.split('-');
                    if (parts.length === 3) return `${parts[2]}/${parts[1]}/${parts[0]}`;
                    return dateString;
                };
                const finalData = { ...formData, servicePeriodStart: fromInputDate(formData.servicePeriodStart), servicePeriodEnd: fromInputDate(formData.servicePeriodEnd) };
                delete finalData.files;
                delete finalData.photos;

                let documentId = tradeId;
                if (!documentId) {
                    const docRef = await addDoc(collection(db, `artifacts/${appId}/public/data/trades`), { ...finalData, createdAt: serverTimestamp(), createdBy: user.uid, files: [], photos: [] });
                    documentId = docRef.id;
                } else {
                    await updateDoc(doc(db, `artifacts/${appId}/public/data/trades`, documentId), { ...finalData, updatedAt: serverTimestamp(), updatedBy: user.uid });
                }

                if (filesToUpload.length > 0) {
                    const tradeRef = doc(db, `artifacts/${appId}/public/data/trades`, documentId);
                    for (const file of filesToUpload) {
                        const filePath = `trades/${documentId}/files/${file.name}`;
                        const fileRef = ref(storage, filePath);
                        await uploadBytes(fileRef, file);
                        const fileUrl = await getDownloadURL(fileRef);
                        await updateDoc(tradeRef, { files: arrayUnion({ name: file.name, url: fileUrl, path: filePath }) });
                    }
                }

                await logActivity(activityType, 'trade', documentId);

                setModalMessage('Operação realizada com sucesso!');
                closeTradeModal();
            }
        } catch (error) {
            console.error("Erro ao salvar a troca: ", error);
            setModalMessage('Ocorreu um erro ao salvar a troca.');
        } finally {
            setIsProcessing(false);
        }
    };

    const handleDeleteTradeFile = async (tradeId, fileToDelete) => {
        try {
            const fileRef = ref(storage, fileToDelete.path);
            await deleteObject(fileRef);
            const tradeRef = doc(db, `artifacts/${appId}/public/data/trades`, tradeId);
            await updateDoc(tradeRef, { files: arrayRemove(fileToDelete) });
            setModalMessage('Arquivo excluído com sucesso!');
            setCurrentTrade(prev => ({ ...prev, files: prev.files.filter(f => f.path !== fileToDelete.path) }));
        } catch (error) {
            console.error("Erro ao excluir arquivo: ", error);
            setModalMessage('Ocorreu um erro ao excluir o arquivo.');
        }
    };

    const handleSavePhoto = async (tradeId, photoBlob, thumbnailBlob) => {
        if (!tradeId || !photoBlob || !thumbnailBlob) {
            setModalMessage("Erro: Faltam dados da foto para salvar.");
            return;
        }
        setIsProcessing(true);
        const photoId = uuidv4();

        const fullPhotoPath = `trades/${tradeId}/photos/${photoId}_full.jpg`;
        const thumbPhotoPath = `trades/${tradeId}/photos/${photoId}_thumb.jpg`;

        const fullPhotoRef = ref(storage, fullPhotoPath);
        const thumbPhotoRef = ref(storage, thumbPhotoPath);

        const metadata = { customMetadata: { 'createdBy': user.uid } };

        try {
            await uploadBytes(fullPhotoRef, photoBlob, metadata);
            const photoUrl = await getDownloadURL(fullPhotoRef);

            await uploadBytes(thumbPhotoRef, thumbnailBlob, metadata);
            const thumbnailUrl = await getDownloadURL(thumbPhotoRef);

            const newPhotoData = {
                id: photoId,
                url: photoUrl,
                thumbnailURL: thumbnailUrl,
                path: fullPhotoPath,
                thumbnailPath: thumbPhotoPath,
                createdAt: new Date(),
                createdBy: user.uid
            };

            const tradeRef = doc(db, `artifacts/${appId}/public/data/trades`, tradeId);
            await updateDoc(tradeRef, { photos: arrayUnion(newPhotoData) });

            setCurrentTrade(prev => ({ ...prev, photos: [...(prev.photos || []), newPhotoData] }));
            setModalMessage("Foto adicionada com sucesso!");

        } catch (error) {
            console.error("Erro ao salvar foto:", error);
            setModalMessage("Falha ao salvar a foto.");
        } finally {
            setIsProcessing(false);
        }
    };

    const handleDeletePhoto = async (tradeId, photoToDelete) => {
        if (!tradeId || !photoToDelete) return;
        setIsProcessing(true);

        try {
            const photoRef = ref(storage, photoToDelete.path);
            await deleteObject(photoRef);

            if (photoToDelete.thumbnailPath) {
                const thumbRef = ref(storage, photoToDelete.thumbnailPath);
                await deleteObject(thumbRef).catch(err => console.warn("Miniatura não encontrada para exclusão, pode já ter sido removida:", err));
            }

            const tradeRef = doc(db, `artifacts/${appId}/public/data/trades`, tradeId);
            await updateDoc(tradeRef, { photos: arrayRemove(photoToDelete) });

            setCurrentTrade(prev => ({ ...prev, photos: prev.photos.filter(p => p.id !== photoToDelete.id) }));
            setModalMessage("Foto excluída com sucesso.");

        } catch (error) {
            console.error("Erro ao excluir foto:", error);
            setModalMessage("Falha ao excluir a foto.");
        } finally {
            setIsProcessing(false);
        }
    };

    const handleDeleteTrade = async (tradeId) => {
        setIsProcessing(true);
        try {
            await deleteDoc(doc(db, `artifacts/${appId}/public/data/trades`, tradeId));
            setModalMessage('Troca excluída com sucesso!');
            closeTradeModal();
        } catch (error) {
            console.error("Erro ao excluir a troca: ", error);
            setModalMessage('Ocorreu um erro ao excluir a troca.');
        } finally {
            setIsProcessing(false);
        }
    };

    const handleDeleteNotification = async (notificationId) => {
        setIsProcessing(true);
        try {
            await deleteDoc(doc(db, `artifacts/${appId}/public/data/irregularity_notifications`, notificationId));
            setModalMessage('Notificação excluída com sucesso!');
            closeNotificationModal();
        } catch (error) {
            console.error("Erro ao excluir a notificação: ", error);
            setModalMessage('Ocorreu um erro ao excluir a notificação.');
        } finally {
            setIsProcessing(false);
        }
    };

    const handleSaveNotificationPhoto = async (notificationId, photoBlob, thumbnailBlob) => {
        if (!notificationId || !photoBlob || !thumbnailBlob) {
            setModalMessage("Erro: Faltam dados da foto para salvar.");
            return;
        }
        setIsProcessing(true);
        const photoId = uuidv4();

        const fullPhotoPath = `irregularity_notifications/${notificationId}/photos/${photoId}_full.jpg`;
        const thumbPhotoPath = `irregularity_notifications/${notificationId}/photos/${photoId}_thumb.jpg`;

        const fullPhotoRef = ref(storage, fullPhotoPath);
        const thumbPhotoRef = ref(storage, thumbPhotoPath);

        const metadata = { customMetadata: { 'createdBy': user.uid } };

        try {
            await uploadBytes(fullPhotoRef, photoBlob, metadata);
            const photoUrl = await getDownloadURL(fullPhotoRef);

            await uploadBytes(thumbPhotoRef, thumbnailBlob, metadata);
            const thumbnailUrl = await getDownloadURL(thumbPhotoRef);

            const newPhotoData = {
                id: photoId,
                url: photoUrl,
                thumbnailURL: thumbnailUrl,
                path: fullPhotoPath,
                thumbnailPath: thumbPhotoPath,
                createdAt: new Date(),
                createdBy: user.uid
            };

            const notificationRef = doc(db, `artifacts/${appId}/public/data/irregularity_notifications`, notificationId);
            await updateDoc(notificationRef, { photos: arrayUnion(newPhotoData) });

            setCurrentNotification(prev => ({ ...prev, photos: [...(prev.photos || []), newPhotoData] }));
            setModalMessage("Foto adicionada com sucesso!");

        } catch (error) {
            console.error("Erro ao salvar foto da notificação:", error);
            setModalMessage("Falha ao salvar a foto.");
        } finally {
            setIsProcessing(false);
        }
    };

    const handleDeleteNotificationPhoto = async (notificationId, photoToDelete) => {
        if (!notificationId || !photoToDelete) return;
        setIsProcessing(true);

        try {
            const photoRef = ref(storage, photoToDelete.path);
            await deleteObject(photoRef);

            if (photoToDelete.thumbnailPath) {
                const thumbRef = ref(storage, photoToDelete.thumbnailPath);
                await deleteObject(thumbRef).catch(err => console.warn("Miniatura não encontrada para exclusão, pode já ter sido removida:", err));
            }

            const notificationRef = doc(db, `artifacts/${appId}/public/data/irregularity_notifications`, notificationId);
            await updateDoc(notificationRef, { photos: arrayRemove(photoToDelete) });

            setCurrentNotification(prev => ({ ...prev, photos: prev.photos.filter(p => p.id !== photoToDelete.id) }));
            setModalMessage("Foto excluída com sucesso.");

        } catch (error) {
            console.error("Erro ao excluir foto da notificação:", error);
            setModalMessage("Falha ao excluir a foto.");
        } finally {
            setIsProcessing(false);
        }
    };


    const handleNotificationClick = useCallback(async (notification) => {
        if (!db) return;

        const notifRef = doc(db, 'notifications', notification.id);
        try {
            await updateDoc(notifRef, { read: true });

            if (window.Android && window.Android.onClearNotification) {
                try {
                    window.Android.onClearNotification(notification.id);
                } catch (e) {
                    console.error("[Android Bridge] Error calling onClearNotification:", e);
                }
            }
        } catch (error) {
            console.error("Erro ao marcar notificação como lida:", error);
            setModalMessage("Não foi possível processar a notificação.");
            return;
        }

        setIsNotificationsModalOpen(false);

        switch (notification.type) {
            case 'newUser':
                setActiveTab('grafico');
                setIsAdminPanelOpen(true);
                break;

            case 'pendencyAssigned':
            case 'pendencyUpdate':
            case 'pendencyMention':
                const pendencyToOpen = pendencies.find(p => p.id === notification.relatedId);
                if (pendencyToOpen) {
                    setActiveTab('pendencias');
                    openPendencyModal(pendencyToOpen);
                } else {
                    setModalMessage("A pendência associada a esta notificação não foi encontrada.");
                }
                break;

            case 'projectUpdate':
            case 'projectMention':
                const projectToOpen = projects.find(p => p.id === notification.relatedId);
                if (projectToOpen) {
                    setActiveTab('projetos');
                    openProjectModal(projectToOpen);
                } else {
                    setModalMessage("O projeto associado a esta notificação não foi encontrado.");
                }
                break;

            case 'notificationNew':
            case 'notificationClosed':
                const notificationToOpen = irregularityNotifications.find(n => n.id === notification.relatedId);
                if (notificationToOpen) {
                    setActiveTab('notificacoes');
                    openNotificationModal(notificationToOpen);
                } else {
                    setModalMessage("A notificação de irregularidade associada não foi encontrada.");
                }
                break;

            default:
                console.warn(`Tipo de notificação não tratado: ${notification.type}`);
                break;
        }
    }, [db, setModalMessage, setIsNotificationsModalOpen, setActiveTab, setIsAdminPanelOpen, pendencies, openPendencyModal, projects, openProjectModal, irregularityNotifications, openNotificationModal]);

    const handleCloseNotification = () => {
        setModalMessage('');
        if (operationSuccess) {
            closeProjectModal();
            closePendencyModal();
            closeTradeModal();
            closeNotificationModal();
            setOperationSuccess(false);
        }
    };

    const handleCloseWhatsNewModal = (dontShowAgain) => {
        if (dontShowAgain) { localStorage.setItem('whatsNewLastSeenVersion', APP_VERSION); }
        setIsWhatsNewModalOpen(false);
    };

    useEffect(() => {
        const urlParams = new URLSearchParams(window.location.search);
        let urlChanged = false;

        const pendencyId = urlParams.get('pendencyId');
        if (pendencyId && pendencies.length > 0) {
            const pendencyToOpen = pendencies.find(p => p.id === pendencyId);
            if (pendencyToOpen) {
                setActiveTab('pendencias');
                openPendencyModal(pendencyToOpen);
                urlParams.delete('pendencyId');
                urlChanged = true;
            }
        }

        const openNotificationId = urlParams.get('openNotification');
        if (openNotificationId && userNotifications.length > 0) {
            const notifToOpen = userNotifications.find(n => n.id === openNotificationId);
            if (notifToOpen) {
                handleNotificationClick(notifToOpen);
                urlParams.delete('openNotification');
                urlChanged = true;
            }
        }

        const notificationId = urlParams.get('notificationId');
        if (notificationId && irregularityNotifications.length > 0) {
            const notifToOpen = irregularityNotifications.find(n => n.id === notificationId);
            if (notifToOpen) {
                if (userData?.role === 'atendimento' || userData?.role === 'comercial') {
                    setModalMessage("Você não tem permissão para visualizar esta notificação.");
                } else {
                    setActiveTab('notificacoes');
                    openNotificationModal(notifToOpen);
                }
                urlParams.delete('notificationId');
                urlChanged = true;
            } else if (!dataLoading) {
                setModalMessage("Notificação não encontrada.");
                urlParams.delete('notificationId');
                urlChanged = true;
            }
        }

        if (urlChanged) {
            const newUrl = window.location.pathname + (urlParams.toString() ? `?${urlParams.toString()}` : '');
            window.history.replaceState({}, document.title, newUrl);
        }
    }, [pendencies, userNotifications, irregularityNotifications, dataLoading, userData?.role, openNotificationModal, handleNotificationClick, openPendencyModal]);

    // Aguarda autenticação, dados do usuário e definição da aba inicial para evitar flash
    if (authLoading || (user && !userData) || (user && userData && userData.role !== 'pending' && activeTab === null)) {
        return (
            <div className="flex justify-center items-center h-screen bg-slate-100 dark:bg-slate-900">
                <div className="animate-spin rounded-full h-32 w-32 border-t-2 border-b-2 border-blue-500"></div>
            </div>
        );
    }

    if (!user) {
        return (
            <Router>
                <Routes>
                    <Route path="/" element={<LoginPage auth={auth} />} />
                    <Route path="/signup" element={<SignUpPage />} />
                    <Route path="*" element={<Navigate to="/" />} />
                </Routes>
            </Router>
        );
    }

    if (userData?.role === 'pending') {
        return <PendingApprovalPage auth={auth} />;
    }

    return (
        <>
            <GlobalStyles />
            <div className="relative min-h-screen overflow-x-hidden bg-slate-100 dark:bg-slate-900 font-sans text-slate-800 dark:text-slate-200" style={{ touchAction: 'pan-y' }}>
                <CelebrationAnimation
                    show={showCelebration}
                    onAnimationEnd={() => setShowCelebration(false)}
                />
                <div className="relative z-[10000]"><NotificationModal message={modalMessageState} onClose={handleCloseNotification} /></div>
                <WhatsNewModal isOpen={isWhatsNewModalOpen} onClose={handleCloseWhatsNewModal} version={APP_VERSION} content={WHATS_NEW_CONTENT} />

                <NewPendencyDialog
                    isOpen={!!newPendencyInfo}
                    onClose={() => setNewPendencyInfo(null)}
                    pendencyId={newPendencyInfo}
                    setModalMessage={setModalMessage}
                />

                <NotificationsModal
                    isOpen={isNotificationsModalOpen}
                    onClose={() => setIsNotificationsModalOpen(false)}
                    notifications={userNotifications}
                    onNotificationClick={handleNotificationClick}
                    onClearAll={handlers.onClearAllNotifications}
                    isProcessing={isProcessing}
                    theme={theme}
                />

                <DuplicatePolesModal isOpen={isDuplicatePolesModalOpen} onClose={() => setIsDuplicatePolesModalOpen(false)} poles={appStats.duplicatePolesList} theme={theme} darkMapEnabled={settings.darkMapEnabled} />
                {isAdminPanelOpen && <AdminPanelModal auth={auth} db={db} storage={storage} appId={appId} onClose={() => setIsAdminPanelOpen(false)} setModalMessage={setModalMessage} currentUser={user} initialSettings={settings} />}
                {isProfileModalOpen && <UserProfileModal currentUser={user} targetUser={user} auth={auth} db={db} storage={storage} onClose={() => setIsProfileModalOpen(false)} setModalMessage={setModalMessage} />}
                {isEffortCalculatorOpen && <EffortCalculatorModal onClose={() => setIsEffortCalculatorOpen(false)} />}

                {isProjectModalOpen && <ProjectModal
                    project={currentProject} onClose={closeProjectModal} cities={citiesData} isProcessing={isProcessing} userRole={userData?.role} currentUser={userData} usersMap={usersMap} theme={theme} darkMapEnabled={settings.darkMapEnabled}
                    onSave={handlers.onSaveProject} onUpdate={handlers.onUpdateProject} onDelete={handlers.onDeleteProject} onAddComment={handlers.onAddProjectComment} onUpdateComment={handlers.onUpdateProjectComment} onDeleteComment={handlers.onDeleteProjectComment} onDeleteFile={handlers.onDeleteProjectFile} onFileDownload={handlers.onFileDownload}
                    onReplacePoles={handlers.onReplaceProjectPoles}
                    allProjects={projects} allPendencies={pendencies} onOpenProject={openProjectModal} onOpenPendency={openPendencyModal}
                />}
                {isPendencyModalOpen && <PendencyModal
                    pendency={currentPendency} onClose={closePendencyModal} cities={citiesData} users={usersList} currentUser={userData} isProcessing={isProcessing}
                    onSave={handlers.onSavePendency} onUpdate={handlers.onUpdatePendency} onDelete={handlers.onDeletePendency} onDeleteFile={handlers.onDeletePendencyFile} onFileDownload={handlers.onFileDownload}
                    onUpdatePendencyField={handlers.onUpdatePendencyField}
                    onAddComment={handlers.onAddPendencyComment} onUpdateComment={handlers.onUpdatePendencyComment} onDeleteComment={handlers.onDeletePendencyComment}
                    usersMap={usersMap}
                    allProjects={projects} allPendencies={pendencies} onOpenProject={openProjectModal} onOpenPendency={openPendencyModal}
                    setModalMessage={setModalMessage}
                    theme={theme}
                />}
                {isTradeModalOpen && <TradeModal
                    isOpen={isTradeModalOpen} onClose={closeTradeModal} onSave={handleSaveTrade} onDelete={handleDeleteTrade} onDeleteFile={handleDeleteTradeFile} onFileDownload={handlers.onFileDownload}
                    trade={currentTrade} cities={citiesData} isProcessing={isProcessing} setModalMessage={setModalMessage} userRole={userData?.role} theme={theme} darkMapEnabled={settings.darkMapEnabled}
                    currentUser={userData} onSavePhoto={handleSavePhoto} onDeletePhoto={handleDeletePhoto}
                    usersMap={usersMap}
                    existingTrades={allTrades}
                />}

                {isTradeShareModalOpen && tradeToShare && <TradeShareModal
                    isOpen={isTradeShareModalOpen}
                    onClose={closeTradeShareModal}
                    trade={tradeToShare}
                />}

                {isNotificationModalOpen && <NewNotificationModal
                    isOpen={isNotificationModalOpen}
                    onClose={closeNotificationModal}
                    onSave={handlers.onSaveNotification}
                    onUpdate={handlers.onUpdateNotification}
                    onDelete={handleDeleteNotification}
                    onAssign={handlers.onAssignNotification}
                    onSavePhoto={handleSaveNotificationPhoto}
                    onDeletePhoto={handleDeleteNotificationPhoto}
                    onCloseNotification={handlers.onCloseNotification}
                    onGenerateReport={handlers.onGenerateNotificationReport}
                    onFileDownload={handlers.onFileDownload}
                    onAbandon={handlers.onAbandonNotification}
                    onMarkAsAnswered={handlers.onMarkAsAnswered}
                    onRestore={handlers.onRestoreNotification}
                    notification={currentNotification}
                    currentUser={user}
                    cities={citiesData}
                    setModalMessage={setModalMessage}
                    isProcessing={isProcessing}
                    userRole={userData?.role}
                    usersList={usersList}
                    theme={theme}
                    settings={settings}
                />}

                {isBulkNotificationModalOpen && <BulkNotificationModal
                    isOpen={isBulkNotificationModalOpen}
                    onClose={() => setIsBulkNotificationModalOpen(false)}
                />}

                <div className="max-w-7xl mx-auto py-4 px-0 sm:px-6 sm:py-6 lg:p-8 pb-16">
                    <Header
                        user={user} userData={userData} notifications={userNotifications} theme={theme}
                        onToggleTheme={() => handleThemeChange(theme === 'light' ? 'dark' : 'light')} onLogout={handleLogout} onOpenProfile={() => setIsProfileModalOpen(true)}
                        onOpenAdminPanel={() => setIsAdminPanelOpen(true)} onOpenNotifications={() => setIsNotificationsModalOpen(true)}
                        onOpenEffortCalculator={() => setIsEffortCalculatorOpen(true)}
                        onOpenAbout={() => setIsAboutModalOpen(true)}
                        showDashboardData={showDashboardData} onToggleDashboardVisibility={() => setShowDashboardData(!showDashboardData)}
                    />
                    <div className="bg-white dark:bg-slate-800/50 rounded-2xl shadow-lg p-2 sm:p-4 backdrop-blur border border-black/5 dark:border-white/5 overflow-x-hidden">
                        <MainTabs
                            activeTab={activeTab}
                            setActiveTab={setActiveTab}
                            userRole={userData?.role}
                            hasUrgentNotifications={hasUrgentNotifications}
                            onOpenAbout={() => setIsAboutModalOpen(true)}
                        />
                        <div className="mt-6 min-h-[450px]">
                            {dataLoading ? (
                                <div className="flex justify-center items-center h-64"><Loader className="animate-spin h-16 w-16 text-blue-500" /></div>
                            ) : connectionError ? (<div className="flex flex-col justify-center items-center h-64 text-center text-red-600 bg-red-50 dark:bg-red-900/20 dark:text-red-400 p-4 rounded-lg"> <AlertTriangle className="w-16 h-16 mb-4" /> <h3 className="text-xl font-bold mb-2">Erro de Conexão</h3> <p className="max-w-md">{connectionError}</p> </div>
                            ) : activeTab === 'grafico' ? (
                                <div className="relative p-4 min-h-[580px] rounded-lg">
                                    <DashboardSubTabs
                                        tabs={visibleDashboardSubTabs}
                                        activeTab={dashboardSubTab}
                                        onTabChange={setDashboardSubTab}
                                    />

                                    <div className={`transition-all duration-300 ${shouldMaskDashboardData ? 'blur-lg' : ''}`}>
                                        <div style={{ display: dashboardSubTab === 'infra' ? 'block' : 'none' }}>
                                            <StatsDisplay
                                                stats={appStats}
                                                onDuplicateClick={handleDuplicateClick}
                                                showData={showDashboardData}
                                            />
                                            <div style={{ width: '100%', height: 450, marginTop: '20px' }}>
                                                {dashboardSubTab === 'infra' && (
                                                    <ResponsiveContainer>
                                                        <BarChart data={chartData} margin={{ top: 20, right: 40, left: 20, bottom: 5 }}>
                                                            <defs><linearGradient id="colorRegularizado" x1="0" y1="0" x2="0" y2="1"><stop offset="5%" stopColor="#3b82f6" stopOpacity={0.9} /><stop offset="95%" stopColor="#2563eb" stopOpacity={1} /></linearGradient><linearGradient id="colorARegularizar" x1="0" y1="0" x2="0" y2="1"><stop offset="5%" stopColor="#6366f1" stopOpacity={0.8} /><stop offset="95%" stopColor="#4f46e5" stopOpacity={0.9} /></linearGradient></defs>
                                                            <CartesianGrid strokeDasharray="3 3" vertical={false} stroke={theme === 'light' ? '#e2e8f0' : '#334155'} />
                                                            <XAxis dataKey="nome" tick={{ fill: theme === 'light' ? '#475569' : '#94a3b8' }} />
                                                            <YAxis yAxisId="left" allowDecimals={false} tick={{ fill: theme === 'light' ? '#475569' : '#94a3b8' }} />
                                                            <YAxis yAxisId="right" orientation="right" allowDecimals={false} tickFormatter={formatCompactCurrency} tick={{ fill: theme === 'light' ? '#475569' : '#94a3b8' }} />
                                                            <Tooltip content={<CustomTooltip />} cursor={{ fill: 'rgba(100, 116, 139, 0.1)' }} />
                                                            <Legend wrapperStyle={{ paddingTop: '20px' }} />
                                                            <Bar yAxisId="left" dataKey="regularizados" stackId="a" name="Regularizados" fill="url(#colorRegularizado)" />
                                                            <Bar yAxisId="left" dataKey="aRegularizar" stackId="a" name="A Regularizar" fill="url(#colorARegularizar)" radius={[8, 8, 0, 0]} />
                                                            <Line yAxisId="right" type="monotone" dataKey="custoAtual" name="Custo Atual (R$)" stroke="#ef4444" strokeWidth={2} dot={{ r: 4 }} activeDot={{ r: 6 }} />
                                                            <Line yAxisId="right" type="monotone" dataKey="custoProjetado" name="Custo Projetado (R$)" stroke="#f97316" strokeWidth={2} strokeDasharray="5 5" dot={{ r: 4 }} activeDot={{ r: 6 }} />
                                                        </BarChart>
                                                    </ResponsiveContainer>
                                                )}
                                            </div>
                                        </div>

                                        <div style={{ display: dashboardSubTab === 'comercial' ? 'block' : 'none' }}>
                                            {dashboardSubTab === 'comercial' && <CommercialDashboard biStats={biStats} theme={theme} />}
                                        </div>

                                        <div style={{ display: dashboardSubTab === 'churn' ? 'block' : 'none' }}>
                                            {dashboardSubTab === 'churn' && <ChurnDashboard biStats={biStats} theme={theme} />}
                                        </div>

                                        {visibleDashboardSubTabs.some(tab => tab.id === 'qualidade') && (<div style={{ display: dashboardSubTab === 'qualidade' ? 'block' : 'none' }}>
                                            {dashboardSubTab === 'qualidade' && <QualityDashboard theme={theme} />}
                                        </div>)}
                                    </div>

                                    {shouldMaskDashboardData && (
                                        <button
                                            type="button"
                                            onClick={() => setShowDashboardData(true)}
                                            title="Exibir dados do gráfico"
                                            aria-label="Exibir dados do gráfico"
                                            className="absolute inset-0 flex flex-col items-center justify-center text-center p-4 z-10 cursor-pointer group focus:outline-none"
                                        >
                                            <Eye className="h-12 w-12 text-slate-500 dark:text-slate-400 mx-auto mb-4 transition-colors group-hover:text-slate-700 dark:group-hover:text-slate-200" />
                                            <p className="font-semibold text-lg text-slate-600 dark:text-slate-300">Dados do painel ocultos</p>
                                            <p className="text-sm text-slate-500 dark:text-slate-400">Clique para exibir.</p>
                                        </button>
                                    )}
                                </div>
                            ) : activeTab === 'pendencias' ? (
                                <PendenciesPage
                                    enrichedPendencies={enrichedPendencies}
                                    userData={userData}
                                    onOpenPendencyModal={openPendencyModal}
                                    showOnlyPending={showOnlyPending}
                                    setShowOnlyPending={setShowOnlyPending}
                                    pendencyStatusChartData={pendencyStatusChartData}
                                    settings={settings}
                                    onTogglePendencyPin={handlers.onTogglePendencyPin}
                                    usersMap={usersMap}
                                />
                            ) : activeTab === 'trocas' ? (
                                <TradesPage
                                    db={db}
                                    user={user}
                                    userData={userData}
                                    settings={settings}
                                    theme={theme}
                                    onOpenTradeModal={openTradeModal}
                                    onOpenTradeShareModal={openTradeShareModal}
                                    showOzmapLayers={true} // <-- Ativando a camada do Ozmap
                                    ozmapProjectId={"BOTUCATU"} // <-- ID do projeto a ser exibido
                                    trades={allTrades}
                                    loading={tradesLoading}
                                    searchTerm={tradeSearchTerm}
                                    onSearchChange={setTradeSearchTerm}
                                />
                            ) : activeTab === 'cobertura' ? (
                                <CoveragePage user={user} db={db} theme={theme} darkMapEnabled={settings.darkMapEnabled} />
                            ) : activeTab === 'notificacoes' ? (
                                <NotificationsPage
                                    db={db}
                                    user={user}
                                    userData={userData}
                                    settings={settings}
                                    theme={theme}
                                    onOpenNotificationModal={openNotificationModal}
                                    onOpenBulkNotificationModal={() => setIsBulkNotificationModalOpen(true)}
                                    notifications={irregularityNotifications}
                                    usersList={usersList}
                                    loading={dataLoading}
                                />
                            ) : activeTab === 'dados' ? (
                                <div className="p-4">
                                    <div className="flex justify-between items-center mb-6">
                                        <h2 className="text-2xl font-bold text-slate-700 dark:text-slate-200">Dados do Dashboard</h2>
                                        {userData?.role === 'administrador' && (
                                            <div className="flex gap-4">
                                                <button
                                                    onClick={handleRefreshOzmap}
                                                    disabled={isProcessing}
                                                    className="flex items-center gap-2 bg-blue-600 text-white font-bold py-2 px-4 rounded-lg hover:bg-blue-700 transition-colors disabled:opacity-50"
                                                >
                                                    <RefreshCw className={`h-5 w-5 ${isProcessing ? 'animate-spin' : ''}`} />
                                                    {isProcessing ? 'Atualizando...' : 'Atualizar Dados Ozmap'}
                                                </button>
                                                <button
                                                    onClick={handleRefreshBi}
                                                    disabled={isProcessing}
                                                    className="flex items-center gap-2 bg-green-600 text-white font-bold py-2 px-4 rounded-lg hover:bg-green-700 transition-colors disabled:opacity-50"
                                                >
                                                    <RefreshCw className={`h-5 w-5 ${isProcessing ? 'animate-spin' : ''}`} />
                                                    {isProcessing ? 'Atualizando...' : 'Atualizar Dados (IXC)'}
                                                </button>
                                            </div>
                                        )}
                                    </div>
                                    {userData?.role !== 'administrador' ? (<p className="text-center text-amber-600 dark:text-amber-400 p-4 bg-amber-50 dark:bg-amber-900/20 rounded-lg">Você não tem permissão para visualizar estes dados.</p>
                                    ) : (
                                        <>
                                            <div className="overflow-x-auto rounded-lg border border-slate-200 dark:border-slate-700">
                                                <table className="min-w-full divide-y divide-slate-200 dark:divide-slate-700 bg-white dark:bg-slate-800">
                                                    <thead className="bg-slate-50 dark:bg-slate-700/50">
                                                        <tr>
                                                            <th className="px-6 py-3 text-left text-xs font-medium text-slate-500 dark:text-slate-400 uppercase tracking-wider">Cidade</th>
                                                            <th className="px-6 py-3 text-left text-xs font-medium text-slate-500 dark:text-slate-400 uppercase tracking-wider">Total</th>
                                                            <th className="px-6 py-3 text-left text-xs font-medium text-slate-500 dark:text-slate-400 uppercase tracking-wider">Licenciados</th>
                                                            <th className="px-6 py-3 text-left text-xs font-medium text-slate-500 dark:text-slate-400 uppercase tracking-wider">Reprovados</th>
                                                            <th className="px-6 py-3 text-left text-xs font-medium text-slate-500 dark:text-slate-400 uppercase tracking-wider">Desconhecidos</th>
                                                            <th className="px-6 py-3 text-left text-xs font-medium text-slate-500 dark:text-slate-400 uppercase tracking-wider">A Licenciar</th>
                                                            <th className="px-6 py-3 text-left text-xs font-medium text-slate-500 dark:text-slate-400 uppercase tracking-wider">% Licenciado</th>
                                                        </tr>
                                                    </thead>
                                                    <tbody className="divide-y divide-slate-200 dark:divide-slate-700">
                                                        {calculatedCitiesData.map(city => {
                                                            const total = city.total || 0;
                                                            // Use backend data if available, otherwise fallback to 0 (or old regularizados if we wanted, but let's stick to new logic)
                                                            const licenciados = city.licenciados || 0;
                                                            const reprovados = city.reprovados || 0;
                                                            const desconhecidos = city.desconhecidos || 0;
                                                            // A Licenciar agora é igual aos Desconhecidos (sem protocolo)
                                                            const aLicenciar = desconhecidos;
                                                            const percentual = total > 0 ? ((licenciados / total) * 100).toFixed(1) : '0.0';

                                                            return (
                                                                <tr key={city.id}>
                                                                    <td className="px-6 py-4 whitespace-nowrap text-sm font-medium text-slate-900 dark:text-slate-100">{city.nome}</td>
                                                                    <td className="px-6 py-4 whitespace-nowrap text-sm text-slate-500 dark:text-slate-400">{total}</td>
                                                                    <td className="px-6 py-4 whitespace-nowrap text-sm text-green-600 dark:text-green-400 font-medium">{licenciados}</td>
                                                                    <td className="px-6 py-4 whitespace-nowrap text-sm text-red-600 dark:text-red-400">{reprovados}</td>
                                                                    <td className="px-6 py-4 whitespace-nowrap text-sm text-slate-500 dark:text-slate-400">{desconhecidos}</td>
                                                                    <td className="px-6 py-4 whitespace-nowrap text-sm text-amber-600 dark:text-amber-400 font-medium">{aLicenciar}</td>
                                                                    <td className="px-6 py-4 whitespace-nowrap text-sm text-blue-600 dark:text-blue-400 font-bold">{percentual}%</td>
                                                                </tr>
                                                            );
                                                        })}
                                                    </tbody>
                                                </table>
                                            </div>
                                        </>
                                    )}
                                </div>
                            ) : activeTab === 'projetos' ? (
                                <ProjectsPage
                                    projects={enrichedProjects}
                                    projectStatusChartData={projectStatusChartData}
                                    userData={userData}
                                    onOpenProjectModal={openProjectModal}
                                    onUpdateProjectStatus={handlers.onUpdateProjectStatus}
                                    theme={theme}
                                    darkMapEnabled={settings.darkMapEnabled}
                                />
                            ) : activeTab === 'monitoramento' ? (
                                <NetworkMonitoringPage
                                    theme={theme}
                                    darkMapEnabled={settings.darkMapEnabled}
                                    onSavePendency={handlers.onSavePendency}
                                    setModalMessage={setModalMessage}
                                    currentUser={userData}
                                    citiesData={citiesData}
                                />
                            ) : null}
                        </div>
                    </div>
                </div>
            </div>
            <StatusBar show={settings.showStatusBar} />

            <AboutModal
                isOpen={isAboutModalOpen}
                onClose={() => setIsAboutModalOpen(false)}
                userName={user?.displayName || userData?.nome || 'Jogador'}
            />
        </>
    );
}
